import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Card,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { NightlightOutlined, StackedLineChart } from "@mui/icons-material";

// Components
import { BrandContext } from "../../App";
import EngagementChart from "../simple/EngagementChart";
import EngagementFilter from "../simple/EngagementFilter";
import EngagementAchievementBox from "../simple/EngagementAchievementBox";
import DefaultButton from "../simple/DefaultButton";

// Hooks & Context
import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../contexts/AuthContext";

const TeamEngagementPanel = () => {
  const { brand } = React.useContext(BrandContext);
  const { loggedInUser, authToken } = useAuth();
  const theme = useTheme();
  const { callAPI } = useAPI();

  const teamId = loggedInUser?.teams?.[0]?.id || null;
  const minCustomInput = 1;
  const maxCustomInput = 120;

  // UI State
  const [filter, setFilter] = useState("all-members");
  const [days, setDays] = useState(7);
  const [customInput, setCustomInput] = useState("");
  const [customInputError, setCustomInputError] = useState(false);

  // 1. Debounce Logic for Custom Day Input
  useEffect(() => {
    if (customInput === "" || customInputError) return;
    const timeOutId = setTimeout(() => setDays(Number(customInput)), 500);
    return () => clearTimeout(timeOutId);
  }, [customInput, customInputError]);

  // 2. Query: Fetch Team Users (Managers only)
  const { data: teamUsers = [] } = useQuery({
    queryKey: ["team_users", teamId],
    queryFn: () =>
      callAPI(`team_users?team_id=${teamId}`, { token: authToken }),
    enabled: !!teamId,
  });

  // 3. Query: Fetch Engagement/Leaderboard Data
  const { data: processedData, isLoading } = useQuery({
    queryKey: ["leaderboard", teamId, filter, days],
    queryFn: async () => {
      const params = new URLSearchParams({
        days: days.toString(),
        team_id: teamId,
      });
      if (filter !== "all-members") params.set("user_id", filter);

      const response = await callAPI(`leaderboard_data?${params.toString()}`, {
        token: authToken,
      });
      return response.data;
    },
    enabled: !!teamId,
    // THE MAGIC: Transform raw data into the shapes the UI needs
    select: (rawItems) => {
      const sorted = [...rawItems].sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      );

      const points = sorted.map((entry) => ({
        userId: entry.user_id,
        date: entry.date,
        numberOfSims: entry.number_of_sims,
        averageScore: entry.average_total_score,
      }));

      if (points.length === 0) return { points: [], stats: {} };

      // Calculate Stat Boxes
      let highestDay = points[0];
      let lowestDay = points[0];
      const userTotals = {};

      points.forEach((p) => {
        if (p.numberOfSims > highestDay.numberOfSims) highestDay = p;
        if (p.numberOfSims < lowestDay.numberOfSims) lowestDay = p;

        if (p.userId && p.userId !== "-") {
          userTotals[p.userId] = (userTotals[p.userId] || 0) + p.numberOfSims;
        }
      });

      const userList = Object.entries(userTotals).map(([id, total]) => ({
        userId: id,
        totalSims: total,
      }));
      userList.sort((a, b) => b.totalSims - a.totalSims);

      return {
        points,
        stats: {
          highestDay,
          lowestDay,
          topUser: userList[0] || null,
          bottomUser: userList[userList.length - 1] || null,
        },
      };
    },
  });

  // Helper for names
  const getUserName = (id) => {
    const found = teamUsers.find((u) => u.id === id);
    return found ? `${found.first_name} ${found.last_name}` : "Unknown User";
  };

  // Handlers
  const handleViewChange = (_, newValue) =>
    newValue !== null && setDays(newValue);

  const handleExport = () => {
    if (!processedData?.points.length) return;
    const headers = ["Date", "User Name", "User ID", "Number of Sims"];
    const csvRows = processedData.points.map((p) =>
      [p.date, `"${getUserName(p.userId)}"`, p.userId, p.numberOfSims].join(
        ",",
      ),
    );
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `engagement_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    link.click();
    URL.revokeObjectURL(url);
  };

  const { points = [], stats = {} } = processedData || {};

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: theme.containers.paddingMobile,
        flexGrow: 1,
        color: theme.palette.text.primary,
        backgroundColor: "transparent",
        border: `2px solid`,
        borderColor: theme.palette.border.tertiary,
        borderRadius: theme.shape.radius,
        boxShadow: "0px 12px 16px -4px rgba(0, 91, 140, 0.10), ...", // truncated for brevity
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
          Team Engagement
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: "italic", mb: 4 }}>
          See how actively your team is engaging with the program.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
            View Range:
          </Typography>
          <ToggleButtonGroup
            value={days}
            exclusive
            onChange={handleViewChange}
            size="small"
            sx={{
              backgroundColor: "transparent",
              height: 43,
              [theme.breakpoints.down("md")]: {
                justifyContent: "space-between",
                width: "100%",
              },
              "& .MuiToggleButton-root": {
                color: theme.palette.foreground.button_secondary,
                textTransform: "none",
                border: "none",
                borderRadius: 2,
                px: theme.customSpacing.lg,
                py: theme.customSpacing.md,
                fontWeight: theme.typography.fontWeightMedium,
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                  color: theme.palette.foreground.badge_icon,
                  border: "1px solid",
                  borderColor: theme.palette.border.tertiary,
                },
                "&:hover": {
                  backgroundColor: "rgba(154, 92, 255, 0.05)",
                },
              },
            }}
          >
            <ToggleButton value={7}>Weekly</ToggleButton>
            <ToggleButton value={31}>Monthly</ToggleButton>
            <TextField
              type="number"
              placeholder={`${minCustomInput}-${maxCustomInput}`}
              size="small"
              error={customInputError}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (val >= minCustomInput && val <= maxCustomInput) {
                  setCustomInput(e.target.value);
                  setCustomInputError(false);
                } else {
                  setCustomInputError(true);
                }
              }}
              inputProps={{
                min: minCustomInput,
                max: maxCustomInput,
                style: {
                  textAlign: "center",
                },
              }}
              sx={{
                mx: theme.customSpacing.lg,
                my: theme.customSpacing.md,
                width: 100,
                input: {
                  color: theme.palette.foreground.button_secondary,
                  fontSize: theme.typography.textXS.fontSize,
                  padding: theme.customSpacing.xs,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.tertiary,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.tertiary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.brand,
                },
              }}
              helperText={
                customInputError
                  ? `${minCustomInput}-${maxCustomInput} days`
                  : ""
              }
            />
          </ToggleButtonGroup>
        </Box>

        <EngagementFilter
          isManager={teamUsers.length > 0}
          teamUsers={teamUsers}
          filter={filter}
          handleChange={(e) => setFilter(e.target.value)}
        />
      </Box>

      <EngagementChart dataPoints={points} />

      <Box sx={{ display: "flex", gap: 3, my: 4, flexWrap: "wrap" }}>
        {stats.highestDay && (
          <EngagementAchievementBox
            title="Most Active Day"
            icon={<StackedLineChart fontSize="small" />}
            date={stats.highestDay.date}
            count={stats.highestDay.numberOfSims}
          />
        )}
        {stats.topUser && (
          <EngagementAchievementBox
            title="Most Active User"
            user={getUserName(stats.topUser.userId)}
            count={stats.topUser.totalSims}
          />
        )}
        {/* Add more stats boxes here following the same pattern */}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <DefaultButton onClick={handleExport} disabled={!points.length}>
          Download CSV
        </DefaultButton>
      </Box>
    </Card>
  );
};

export default TeamEngagementPanel;
